import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { crmService } from '@/services/crmService';
import type { Contact, Communication, Deal, Appointment } from '@/types/models';

export function useContacts() {
  return useQuery({ queryKey: ['crm', 'contacts'], queryFn: () => crmService.getContacts() });
}

export function useContact(id: string) {
  return useQuery({ queryKey: ['crm', 'contacts', id], queryFn: () => crmService.getContact(id), enabled: !!id });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Contact>) => crmService.createContact(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'contacts'] }),
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Contact> }) => crmService.updateContact(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'contacts'] }),
  });
}

export function useCommunications(contactId: string) {
  return useQuery({
    queryKey: ['crm', 'communications', contactId],
    queryFn: () => crmService.getCommunications(contactId),
    enabled: !!contactId,
  });
}

export function useLogCommunication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, data }: { contactId: string; data: Partial<Communication> }) =>
      crmService.logCommunication(contactId, data),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['crm', 'communications', vars.contactId] }),
  });
}

export function useDeals() {
  return useQuery({ queryKey: ['crm', 'deals'], queryFn: () => crmService.getDeals() });
}

export function useDeal(id: string) {
  return useQuery({ queryKey: ['crm', 'deals', id], queryFn: () => crmService.getDeal(id), enabled: !!id });
}

export function useCreateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Deal>) => crmService.createDeal(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'deals'] }),
  });
}

export function useUpdateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Deal> }) => crmService.updateDeal(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'deals'] }),
  });
}

export function useAppointments() {
  return useQuery({ queryKey: ['crm', 'appointments'], queryFn: () => crmService.getAppointments() });
}

export function useCreateAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Appointment>) => crmService.createAppointment(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'appointments'] }),
  });
}

export function useCrmDashboard() {
  return useQuery({ queryKey: ['crm', 'dashboard'], queryFn: () => crmService.getDashboard() });
}
